import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { colorPickerDriverFactory } from './ColorPicker.driver';
import { ColorPicker } from '.';
import eventually from 'wix-eventually';

describe('ColorPicker', () => {
  const createDriver = createRendererWithUniDriver(colorPickerDriverFactory);
  const bootstrap = (props = {}) => {
    const dataHook = 'compDataHook';
    const compProps = {
      'data-hook': dataHook,
      onChange: () => {},
      ...props,
    };
    return createDriver(<ColorPicker {...compProps} />);
  };

  it('should render', async () => {
    const { driver } = bootstrap();
    expect(await driver.exists()).toBe(true);
  });

  it('should call onChange callback (select by index)', async () => {
    const onChange = jest.fn();
    const { driver } = bootstrap({
      children: <ColorPicker.Item aria-label="red color" value="red" />,
      onChange,
    });

    await driver.selectByIndex(0);

    await eventually(() => expect(onChange).toBeCalled);
  });

  it('should call onChange callback (select by color)', async () => {
    const onChange = jest.fn();
    const color = 'red';
    const { driver } = bootstrap({
      children: <ColorPicker.Item aria-label="red color" value={color} />,
      onChange,
    });

    await driver.selectByColor(color);

    await eventually(() => expect(onChange).toBeCalled);
  });

  it('should call onChange callback with proper value', async () => {
    const onChange = jest.fn();
    const value = '#000000';
    const label = 'black';
    const firstId = 'first-id';
    const secondId = 'second-id';

    const { driver } = bootstrap({
      children: [
        <ColorPicker.Item
          key={0}
          aria-label={label}
          value={value}
          id={firstId}
        />,
        <ColorPicker.Item
          key={1}
          aria-label={label}
          value={value}
          id={secondId}
        />,
      ],
      onChange,
    });

    await driver.selectByIndex(1);

    await eventually(() => expect(onChange).toBeCalled);

    expect(onChange).toBeCalledTimes(1);
    expect(onChange).toHaveBeenCalledWith(
      expect.objectContaining({
        value,
      }),
      secondId,
    );
  });

  it('should support ColorPickerItemDriver (disabled, isCrossedOut)', async () => {
    const onChange = jest.fn();
    const { driver } = bootstrap({
      children: (
        <>
          <ColorPicker.Item aria-label="red color" value="red" isCrossedOut />
          <ColorPicker.Item aria-label="red color" value="blue" disabled />
        </>
      ),
      onChange,
    });

    const itemDriverFirst = driver.getItemAt(0);
    const itemDriverSecond = driver.getItemAt(1);

    expect(await itemDriverFirst.isCrossedOut()).toBe(true);
    expect(await itemDriverFirst.isDisabled()).toBe(false);

    expect(await itemDriverSecond.isCrossedOut()).toBe(false);
    expect(await itemDriverSecond.isDisabled()).toBe(true);
  });

  it('should support ColorPickerItemDriver (tooltip)', async () => {
    const onChange = jest.fn();
    const color = 'red';
    const { driver } = bootstrap({
      children: (
        <ColorPicker.Item
          aria-label="red color"
          value={color}
          tooltip="Hello"
          tooltipDataHook="tooltip-data-hook"
        />
      ),
      onChange,
    });

    const itemDriverFirst = driver.getItemAt(0);

    expect(await itemDriverFirst.getTooltipText()).toBe('Hello');
  });

  it('should support ColorPickerItemDriver isChecked', async () => {
    const onChange = jest.fn();
    const { driver } = bootstrap({
      children: (
        <>
          <ColorPicker.Item aria-label="red color" value="red" checked />
          <ColorPicker.Item aria-label="red color" value="blue" />
        </>
      ),
      onChange,
    });

    const itemDriverFirst = driver.getItemAt(0);
    const itemDriverSecond = driver.getItemAt(1);

    expect(await itemDriverFirst.isChecked()).toBe(true);
    expect(await itemDriverSecond.isChecked()).toBe(false);
  });

  it('should  getItemAt', async () => {
    const onChange = jest.fn();

    const { driver } = createDriver(
      <ColorPicker onChange={onChange}>
        <ColorPicker.Item aria-label="red color" value="red" tooltip="Red" />
        <ColorPicker.Item aria-label="blue color" value="blue" tooltip="Blue" />
      </ColorPicker>,
    );

    const itemDriverFirst = driver.getItemAt(0);
    const itemDriverSecond = driver.getItemAt(1);

    expect(await itemDriverFirst.getTooltipText()).toBe('Red');
    expect(await itemDriverSecond.getTooltipText()).toBe('Blue');
  });

  it('should support suffix', async () => {
    const { driver } = bootstrap({
      children: (
        <>
          <ColorPicker.Item aria-label="blue color" value="blue" />
          <ColorPicker.Item aria-label="red color" value="red" />
        </>
      ),
      suffix: <div>Hello from suffix!</div>,
    });

    const suffix = driver.getSuffix();

    expect(await suffix.exists()).toBe(true);
  });

  it('should support on Loading state', async () => {
    const { driver } = bootstrap({
      children: (
        <>
          <ColorPicker.Item aria-label="blue color" value="blue" />
          <ColorPicker.Item aria-label="red color" value="red" />
        </>
      ),
      isLoading: true,
    });
    expect(await driver.getIsLoading()).toBe(true);
  });

  it('should display a label with an asterisk when both required and showAsterisk are true', async () => {
    const { driver } = bootstrap({
      children: (
        <>
          <ColorPicker.Item aria-label="red color" value="red" />
          <ColorPicker.Item aria-label="red color" value="blue" />
        </>
      ),
      label: 'Color',
      required: true,
      showAsterisk: true,
    });
    expect(await driver.getLabelAsterisk().exists()).toBe(true);
  });
  it('should not display a label with an asterisk when only required is true', async () => {
    const { driver } = bootstrap({
      children: (
        <>
          <ColorPicker.Item aria-label="red color" value="red" />
          <ColorPicker.Item aria-label="red color" value="blue" />
        </>
      ),
      label: 'Color',
      required: true,
    });
    expect(await driver.getLabelAsterisk().exists()).toBe(false);
  });
});
